import React, { useEffect, useMemo, } from 'react';
import { pick, sortBy, keyBy, orderBy, } from 'lodash';
import { Button, Tooltip } from 'reactstrap';
import classnames from 'classnames';
import { useToggle, useAsync, } from 'react-use';
import qs from 'qs';
import { parseISO, format as formatDate, startOfDay, addHours, isSameDay, } from 'date-fns';
import ja from 'date-fns/locale/ja';
import en from 'date-fns/locale/en-us';
import sanitizeHtml from 'sanitize-html';

import { activateRichTextHtml } from '../../util';
import { i18nField } from '../../shared/i18n';
import firebase, { functions } from '../../firebase';
import useAppTitle from '../hooks/useAppTitle';
import useLocale from '../hooks/useLocale';
import TenantUserPage from '../hocs/TenantUserPage';
import LectureTitle from '../LectureTitle';
import AppButton from '../AppButton';
import ProgressButton from '../ProgressButton';
import AppCard from '../AppCard';
import useQueryParams from '../hooks/useQueryParams';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import TenantLink from '../TenantLink';
import RichTextContent from '../RichTextContent';

const db = firebase.firestore();
const eventsRef = db.collection('events');
const questionsRef = db.collection('questions');
const { max } = Math;
const fetchPublishedEntries = functions.httpsCallable('fetchPublishedEntries');

function Event(props) {
  const {
    user,
    tenant,
    location,
    match: {
      params: { eventId },
    },
    setLang,
    translate,
    toggleLoginForm,
  } = props;
  const [showFollowHelpTooltip, toggleFollowHelpTooltip] = useToggle(false);
  const queryParams = useQueryParams();
  const { key: theDayKey } = qs.parse(window.location.search.slice(1));
  const event = useDocumentSubscription(eventsRef.doc(eventId), [eventId]);
  const forceKey = useDocumentSubscription(queryParams.forceKey && event?.ref.collection('forceKeys').doc(queryParams.forceKey), [event]);
  const isForce = forceKey?.createdAt.toDate() >= startOfDay(new Date());
  const eventLectureAddNotification = useDocumentSubscription(db.collection('eventLectureAddNotifications').doc([eventId, user?.id].join('__')), [eventId, user?.id]);
  const isTheDayEvent = !!theDayKey && !!(event && event.theDayKey) && theDayKey === event.theDayKey;
  const lectures = useCollectionSubscription(eventsRef.doc(eventId).collection('lectures').orderBy('date'), [eventId]);
  const eventProductTypes = sortBy(useCollectionSubscriptionInTenant(db.collection('eventProductTypes')), _ => _.createdAt.toDate());
  const lectureTypes = sortBy(useCollectionSubscriptionInTenant(db.collection('lectureTypes')), _ => _.createdAt.toDate());
  const questions = useCollectionSubscriptionInTenant(questionsRef);
  const eventProductTypesById = keyBy(eventProductTypes, 'id');
  const questionsById = keyBy(questions, 'id');
  const lectureTypesById = keyBy(lectureTypes, 'id');
  const now = new Date();
  const targetLectureId = queryParams.lectureId;
  const lecturesWithInfo = lectures.filter(_ => !_.isHidden || _.id === targetLectureId).map((lecture) => {
    const { date, frames: lectureFrames = [] } = lecture;
    const unendedLectureFrames = lectureFrames.filter(
      (_) =>
        addHours(
          parseISO(formatDate(date.toDate(), 'yyyy-MM-dd') + formatDate(_.endAt.toDate(), ' HH:mm')),
          -event?.lectureFrameExpireHours || 0
        ) >= now
    );
    const isToday = isSameDay(date.toDate(), new Date());
    const availableLectureFrames = unendedLectureFrames
      .map((lectureFrame, frameIndex) => {
        const { capacity = 0, priorCapacity = 0, entriedCount = 0 } = lectureFrame;
        const displayCapacity = isTheDayEvent && isToday ? capacity : priorCapacity;
        const displayLeftCapacity = displayCapacity - entriedCount;
        const totalLeftCapacity = capacity - entriedCount;
        const isOnlyTheDayAvailable = displayLeftCapacity <= 0 && totalLeftCapacity > 0;
        return {
          ...lectureFrame,
          displayLeftCapacity,
          totalLeftCapacity,
          isOnlyTheDayAvailable,
        };
      })
      .filter((_) => _.totalLeftCapacity > 0);
    return {
      ...lecture,
      unendedLectureFrames,
      availableLectureFrames,
    };
  });
  const invitedUsers = useCollectionSubscription(
    user && eventsRef.doc(eventId).collection('invitedUsers').where('uid', '==', user.uid).limit(1),
    [eventId, user]
  );
  const isInvited = invitedUsers.length === 1;
  const cancellationNotices = useCollectionSubscription(
    user && eventsRef.doc(eventId).collection('cancellationNotices').where('createdBy.uid', '==', user.uid),
    [eventId, user]
  );
  const { value: publishedEntriesData, loading: isLoadingPublishedEntries = false } = useAsync(async () => {
    const { data } = await fetchPublishedEntries({ eventId });
    return data?.entries.map(_ => ({
      ..._,
      createdAt: { toDate: () => new Date(_.createdAt._seconds * 1000), },
    })) ?? [];
  }, [eventId]);
  const publishedEntries = useMemo(_ => orderBy(publishedEntriesData ?? [], _ => _.createdAt.toDate(), 'desc'), [publishedEntriesData]);
  const isAllFull = lecturesWithInfo.every((_) => _.availableLectureFrames.length === 0);
  const waitlistedLectureIds = cancellationNotices.map(_ => _.lectureId);
  const displayedLectures = lecturesWithInfo.filter(_ => _.unendedLectureFrames.length > 0);
  const waitlistedLectures = displayedLectures.filter(_ => waitlistedLectureIds.includes(_.id));
  const hasUnwaitlistedFullLecture = displayedLectures.some(_ => _.abortedAt == null && _.availableLectureFrames.length === 0 && !waitlistedLectureIds.includes(_.id));
  const onClickNewLectureNotification = async () => {
    if(user == null) return toggleLoginForm(true);
    if(!window.confirm(`フォローしますか？(1年間有効です)`)) return;
                  
    await db.collection('eventLectureAddNotifications').doc([event.id, user?.id].join('__')).set({
      eventId: event.id,
      createdBy: pick(user, ['id', 'uid', 'email', 'displayName']),
      createdAt: new Date(),
    });
  };
  const onClickRemoveNewLectureNotification = async () => {
    if(!window.confirm(`フォローをやめますか？`)) return;
                  
    await eventLectureAddNotification.ref.delete();
  };
  useAppTitle(event?.name + ' 申込トップ');
  useEffect(() => {
    event?.lang && setLang(event.lang);
  }, [event?.lang]);

  return (
    event != null && translate(
      <div>
        <div className="event container pt-5 position-relative">
          {isForce && (
            <div className="alert alert-warning text-center font-weight-bold">
              <span className="fas fa-exclamation-triangle mr-2" />
              強制申込モード
            </div>
          )}
          <div className="text-center mb-3">
            <h4>{event.name}</h4>
            {user != null && (['admin', 'staff'].includes(user.role) || isInvited) && (
              <div>
                <TenantLink
                  className="text-muted"
                  to={`/events/${eventId}/detail`}
                  style={{ textDecoration: 'underline' }}
                >
                  イベント詳細を見る
                </TenantLink>
              </div>
            )}
          </div>
          <div className="row mt-3">
            <div className="col-lg-8 offset-lg-2">
              <div className="d-flex justify-content-end">
                {
                  isTheDayEvent ? (
                    <div className="text-muted">
                      (当日予約)
                    </div>
                  ) : (
                    eventLectureAddNotification == null ? (
                      <div className="d-flex align-items-center gap-2">
                        <AppButton Component={ProgressButton} color="dark-grey" size="sm" process={onClickNewLectureNotification}>
                          フォローする
                        </AppButton>
                        {event?.followHelpText && (
                          <>
                            <span id="followHelpIcon" className="far fa-question-circle text-muted" style={{ cursor: 'pointer' }} />
                            <Tooltip placement="top" isOpen={showFollowHelpTooltip} target="followHelpIcon" toggle={toggleFollowHelpTooltip} style={{ maxWidth: '300px' }}>
                              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.followHelpText, { allowedTags: ['br', 'b', 'i', 'u', 'strong', 'em'] }) }} />
                            </Tooltip>
                          </>
                        )}
                      </div>
                    ) : (
                      <AppButton Component={ProgressButton} color="danger" size="sm" outline process={onClickRemoveNewLectureNotification}>
                        フォローをやめる
                      </AppButton>
                    )
                  )
                }
              </div>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-lg-8 offset-lg-2">
              {!isForce && waitlistedLectures.length > 0 ? (
                <div className="alert alert-success mt-2 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
                  <div>
                    <div className="font-weight-bold">
                      <span className="fas fa-check mr-1" />
                      キャンセル待ち中
                    </div>
                    {waitlistedLectures.map(lecture => (
                      <div key={lecture.id}>
                        {formatDate(lecture.date.toDate(), 'yyyy/MM/dd (iii)', { locale: event.lang === 'en' ? en : ja })} {eventProductTypesById[lecture.productType]?.[i18nField(event.lang, 'name')]}
                      </div>
                    ))}
                  </div>
                  {hasUnwaitlistedFullLecture && (
                    <Button color="warning" tag={TenantLink} to={`/events/${eventId}/entries/new${location.search}`}>
                      他にもキャンセル待ちする
                    </Button>
                  )}
                </div>
              ) : isAllFull && !isForce && (
                <div className="alert alert-warning mt-2 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
                  <div>
                    このイベントは満員です。<br />
                    日程ごとにキャンセル待ち登録ができます。
                  </div>
                  <Button color="warning" tag={TenantLink} to={`/events/${eventId}/entries/new${location.search}`}>
                    キャンセル待ちする
                  </Button>
                </div>
              )}
              {event.description && (
                <div className="rounded border border-info p-3">
                  <RichTextContent html={event.description || ''} />
                </div>
              )}
              {publishedEntries.length > 0 && (
                <div className="mt-4">
                  <h5 className="text-center mb-4">参加者の声</h5>
                  <div className="d-flex flex-column gap-3">
                    {publishedEntries.map((entry) => (
                      <div key={entry.id} className="border rounded p-3">
                        <div className="d-flex flex-column gap-3">
                          {entry.laterAnswers && Object.entries(entry.laterAnswers).map(([questionId, answer]) => (
                            <div key={questionId}>
                              <div className="small text-muted">{questionsById[questionId]?.description || questionId}</div>
                              <div>{answer}</div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 d-flex justify-content-end gap-1 small">
                          <div>{formatDate(entry.createdAt.toDate(), 'yyyy/MM/dd')}</div>
                          <div>{entry.createdBy.displayName?.slice(0, 2)}{Array(max(0, (entry.createdBy.displayName?.length || 0) - 2)).fill().map(_ => '*').join('')}様</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-lg-8 offset-lg-2">
              {lecturesWithInfo.map((lecture) => {
                const { id } = lecture;
                const isOnCancellationWaitlist = cancellationNotices.some(_ => _.lectureId === id);
                return <LectureCard key={id} lecture={lecture} user={user} isOnCancellationWaitlist={isOnCancellationWaitlist} {...{ isForce, event, eventProductTypesById, lectureTypesById, }} {...props} />;
              })}
            </div>
          </div>
          <div className="mt-5 position-sticky" style={{ bottom: 10 }}>
            <div className="row">
              <div className="col-lg-8 offset-lg-2">
                <AppButton
                  block
                  color="primary"
                  size="lg"
                  tag={TenantLink}
                  to={`/events/${eventId}/entries/new${location.search}`}
                >
                  このイベントに申し込む
                </AppButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

Event.preview = true;

export default TenantUserPage(Event);

function LectureCard(props) {
  const { isForce, event, lecture, user, isOnCancellationWaitlist, eventProductTypesById, lectureTypesById, location, match: { params: { eventId }}} = props;
  const { abortedAt, unendedLectureFrames, availableLectureFrames } = lecture;
  const [showsAvailability, toggleAvailability] = useToggle();
  if (unendedLectureFrames.length === 0) return null;

  return (
    <AppCard className="p-3 p-md-4 mb-3 d-block">
      <div>
        <LectureTitle {...lecture} {...{ event, eventProductTypesById, lectureTypesById, }} />
      </div>
      {
        lecture.entryStartDate?.toDate() > new Date() && (
          <div className="text-danger">
            ※ {formatDate(lecture.entryStartDate.toDate(), 'yyyy/MM/dd')}申込開始
          </div>
        )
      }
      {abortedAt != null ? (
        <div className="text-danger">中止しました</div>
      ) : availableLectureFrames.length > 0 ? (
        availableLectureFrames.every(_ => _.isOnlyTheDayAvailable) ? (
          <div className="text-info">当日受付</div>
        ) : (
          <Button color="link" onClick={toggleAvailability}>
            <span className={classnames('fas mr-1', { 'fa-plus': !showsAvailability, 'fa-minus': showsAvailability })} />
            {showsAvailability ? '空き状況を閉じる' : '空き状況を見る'}
          </Button>
        )
      ) : (
        !isForce && (
          <div className="mt-1 d-flex justify-content-between align-items-start">
            <div className="text-danger">満員</div>
            <div className="d-flex flex-column align-items-end">
              {isOnCancellationWaitlist ? (
                <>
                  <span className="badge badge-success">
                    <span className="fas fa-check mr-1" />
                    キャンセル待ち登録中
                  </span>
                  <div className="text-muted mt-1 text-right" style={{ fontSize: '0.75rem' }}>
                    空きが出た際にメールで通知します。
                  </div>
                </>
              ) : (
                <Button color="warning" size="sm" tag={TenantLink} to={`/events/${eventId}/entries/new${location.search}`}>キャンセル待ち</Button>
              )}
            </div>
          </div>
        )
      )}
      {showsAvailability && (
        <div>
          {availableLectureFrames.map((lectureFrame, frameIndex) => {
            const { startAt, endAt, displayLeftCapacity, isOnlyTheDayAvailable, lectureType } = lectureFrame;
            const leftCapacityDisplay = isOnlyTheDayAvailable
              ? '当日受付'
              : ['admin', 'staff'].includes(user?.role)
              ? max(displayLeftCapacity, 0)
              : displayLeftCapacity > 0
              ? '○'
              : isForce
              ? ''
              : '満席';
            return (
              <div key={frameIndex} className="d-flex flex-wrap gap-2">
                <span>{formatDate(startAt.toDate(), 'HH:mm')}</span>
                {<span style={{ minWidth: 220 }}>{lectureTypesById[lectureType]?.[i18nField(event?.lang, 'name')]}</span>}
                <span>{leftCapacityDisplay}</span>
              </div>
            );
          })}
        </div>
      )}
    </AppCard>
  );
}
